import { useEffect, useState } from 'react';
import { Globe, ShieldCheck, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Detail = { label: string; value: string; kind: string };
type Insurance = {
  id: string; name: string; portal_url: string | null; comms_email: string | null;
  depreciation_recovery_days: number | null; phones: string[]; how_to_work: string | null; details: Detail[];
};

/**
 * Nombre de aseguradora clickable → abre una tarjeta con TODA la info guardada en
 * la KB "Insurance" (importada del board de Monday): website, emails, teléfonos,
 * links de file/upload/tracking, cómo se filea, notas, estimating, etc. Reusable
 * en cualquier parte donde se muestre la aseguradora. Busca por NOMBRE (los claims
 * guardan el nombre en texto).
 */
export function InsuranceLink({ name }: { name: string | null | undefined }) {
  const [open, setOpen] = useState(false);
  if (!name || !name.trim()) return <>—</>;
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--link)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline', textAlign: 'left', overflowWrap: 'anywhere' }}
      >
        {name}
      </button>
      {open && <InsuranceModal name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

function DetailRow({ d }: { d: Detail }) {
  const href = d.kind === 'email' ? `mailto:${d.value}` : d.kind === 'phone' ? `tel:${d.value.replace(/[^0-9+]/g, '')}` : d.kind === 'link' ? (/^https?:\/\//i.test(d.value) ? d.value : `https://${d.value}`) : null;
  if (d.kind === 'longtext') {
    return (
      <div style={{ padding: '8px 0', borderTop: '0.5px solid var(--line)' }}>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 2 }}>{d.label}</div>
        <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{d.value}</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: '0.5px solid var(--line)' }}>
      <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{d.label}</span>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', textAlign: 'right', overflowWrap: 'anywhere', maxWidth: '62%' }}>
        {href ? <a href={href} target="_blank" rel="noopener noreferrer">{d.kind === 'link' ? (d.value.replace(/^https?:\/\//, '').slice(0, 40)) : d.value}</a> : d.value}
      </span>
    </div>
  );
}

function InsuranceModal({ name, onClose }: { name: string; onClose: () => void }) {
  const { t } = useI18n();
  const [ins, setIns] = useState<Insurance | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'none'>('loading');
  useEffect(() => {
    api.get<{ insurance: Insurance | null }>(`/api/insurance-companies/lookup?name=${encodeURIComponent(name)}`)
      .then((r) => { setIns(r.insurance); setState(r.insurance ? 'ok' : 'none'); })
      .catch(() => setState('none'));
  }, [name]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 460, width: '100%', maxHeight: '78dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, background: 'var(--card)' }}>
          <h2 className="card-title" style={{ margin: 0 }}><ShieldCheck size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {ins?.name || name}</h2>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>
        {state === 'loading' ? (
          <div className="muted" style={{ marginTop: 12 }}>{t('common.loadingDots')}</div>
        ) : state === 'none' ? (
          <div className="muted" style={{ marginTop: 12, fontSize: 'var(--fs-sm)' }}>{t('ins.noInfo')}</div>
        ) : ins ? (
          <div style={{ marginTop: 10 }}>
            {ins.portal_url && (
              <a className="btn sm" href={/^https?:\/\//i.test(ins.portal_url) ? ins.portal_url : `https://${ins.portal_url}`} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 8, display: 'inline-block', textDecoration: 'none' }}><Globe size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ins.openPortal')} ↗</a>
            )}
            {ins.depreciation_recovery_days != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: '0.5px solid var(--line)' }}>
                <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('ins.depDays')}</span>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>{ins.depreciation_recovery_days} {t('ins.days')}</span>
              </div>
            )}
            {ins.details.map((d, i) => <DetailRow key={`${d.label}-${i}`} d={d} />)}
            {ins.details.length === 0 && ins.depreciation_recovery_days == null && !ins.portal_url && (
              <div className="muted" style={{ marginTop: 8, fontSize: 'var(--fs-sm)' }}>{t('ins.noInfo')}</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
